import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CreditCard, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { storage } from '@/utils/storage';
import { SidebarModal } from '@/components/sidebar-modal';

export const Route = createFileRoute('/_authenticated/profile/')({
  component: ProfilePage
});

function ProfilePage() {
  const [userData, setUserData] = useState(() => ({
    email: storage.getUserEmail() || '',
    firstName: storage.getUserFirstName() || '',
    lastName: storage.getUserLastName() || '',
    company: storage.getUserCompany() || ''
  }));

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSaveAccount = async () => {};

  const handleChangePassword = async () => {};

  const sections = [
    {
      key: 'account',
      label: 'Mon compte',
      icon: User,
      content: (
        <div className="space-y-6 max-w-2xl">
          <div>
            <h2 className="text-xl font-semibold mb-2">Informations personnelles</h2>
            <p className="text-sm text-muted-foreground mb-6">Gérez vos informations de compte et vos préférences.</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Informations de base</CardTitle>
              <CardDescription>Mettez à jour vos informations personnelles.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={userData.email}
                  onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">L&apos;email ne peut pas être modifié.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Prénom</Label>
                  <Input
                    disabled
                    id="firstName"
                    type="text"
                    placeholder="Votre prénom"
                    value={userData.firstName}
                    onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nom</Label>
                  <Input
                    disabled
                    id="lastName"
                    type="text"
                    placeholder="Votre nom"
                    value={userData.lastName}
                    onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Entreprise</Label>
                <Input
                  disabled
                  id="company"
                  type="text"
                  placeholder="Nom de votre entreprise"
                  value={userData.company}
                  onChange={(e) => setUserData({ ...userData, company: e.target.value })}
                />
              </div>

              <Button onClick={handleSaveAccount} className="w-full sm:w-auto" disabled>
                Enregistrer les modifications
              </Button>
            </CardContent>
          </Card>

          <Separator />

          <Card>
            <CardHeader>
              <CardTitle>Modifier le mot de passe</CardTitle>
              <CardDescription>Changez votre mot de passe pour sécuriser votre compte.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                <Input
                  disabled
                  id="currentPassword"
                  type="password"
                  placeholder="••••••••"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <Input
                  disabled
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</Label>
                <Input
                  disabled
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                />
              </div>

              <Button onClick={handleChangePassword} className="w-full sm:w-auto" disabled>
                Modifier le mot de passe
              </Button>
            </CardContent>
          </Card>
        </div>
      )
    },
    {
      key: 'subscription',
      label: 'Mon abonnement',
      icon: CreditCard,
      content: (
        <div className="space-y-6 max-w-2xl">
          <div>
            <h2 className="text-xl font-semibold mb-2">Mon abonnement</h2>
            <p className="text-sm text-muted-foreground mb-6">Gérez votre abonnement et vos options de facturation.</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Phase pilote</CardTitle>
              <CardDescription>BeSpark est actuellement en phase pilote.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-6 bg-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <CreditCard className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Accès complet aux fonctionnalités</h3>
                    <p className="text-sm text-muted-foreground">
                      En tant que participant à la phase pilote, vous avez accès à toutes les fonctionnalités de
                      BeSpark sans restriction. Cela inclut :
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc list-inside">
                      <li>Journal des Idées avec analyse IA</li>
                      <li>Interviews vocales illimitées</li>
                      <li>Campagnes de feedback</li>
                      <li>Roadmap et priorisation automatique</li>
                      <li>Propagation automatique des invitations</li>
                    </ul>
                    <p className="mt-4 text-sm text-muted-foreground">
                      Nous vous remercions de votre participation et de vos retours qui nous aident à améliorer le
                      produit. Nous vous tiendrons informé des évolutions et des options d&apos;abonnement à venir.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }
  ];

  return <SidebarModal title="Mon profil" sections={sections} defaultSection="account" />;
}
